package fr.frux.shared.dto

import kotlinx.serialization.Serializable

@Serializable
enum class SpeciesFormDto { ARBRE, ARBUSTE, HERBACEE, LIANE }

/** Portee du referentiel (ADR 0036) : arbre, ou culture reservee aux plantations de lieu. */
@Serializable
enum class SpeciesScopeDto { TREE, SITE_CROP }

@Serializable
enum class HarvestPartDto { FRUIT, FLEUR, FEUILLE, NOIX, RACINE }

/**
 * Toxicite crue de la partie recoltee (ADR 0015) : c'est un attribut de la partie, pas du taxon --
 * les fleurs de sureau se mangent crues, ses baies non. Contenu editorial pur, jamais vote.
 */
@Serializable
enum class RawEdibilityDto { EDIBLE_RAW, REQUIRES_PREPARATION, TOXIC_RAW }

/**
 * Gravite d'un sosie dangereux (ADR 0015). `LETHAL` gouverne le bandeau rouge de la fiche arbre --
 * le seul rouge de l'app, reserve a cette valeur croisee avec une identification non sure.
 */
@Serializable
enum class LookalikeSeverityDto { TOXIC, LETHAL }

/**
 * `monthEnd` peut etre inferieur a `monthStart` : la fenetre chevauche l'annee civile. Tout client
 * qui evalue « de saison » doit gerer ce cas, pas un simple intervalle.
 */
@Serializable
data class SpeciesHarvestDto(
    val part: HarvestPartDto,
    val monthStart: Int,
    val monthEnd: Int,
    val notes: String? = null,
    val rawEdibility: RawEdibilityDto = RawEdibilityDto.EDIBLE_RAW,
)

/**
 * Sosie dangereux d'une espece du catalogue (ADR 0015). Jamais un `SpeciesRefDto` : le sosie n'est
 * pas une espece recoltable et n'entre pas au referentiel -- seuls son nom, sa gravite et une note
 * d'identification redigee (jamais copiee, cf. `SpeciesCatalogCodec`) sont portes ici.
 */
@Serializable
data class SpeciesLookalikeDto(
    val scientificName: String,
    val commonNameFr: String,
    val severity: LookalikeSeverityDto,
    val distinguishingNote: String,
    val sourceUrl: String,
)

/**
 * Photo d'illustration de l'espèce (#389, ADR 0015 amendement 2026-09-25), jamais une photo d'un
 * arbre. Résolue par le serveur : celle de l'espèce, sinon celle de son nœud `rollupId`, sinon
 * absente — le client n'a aucune résolution à faire, même patron que `harvests`/`lookalikes`.
 */
@Serializable
data class SpeciesPhotoDto(
    val url: String,
    val source: String,
    val sourceUrl: String,
    val license: String,
    val author: String? = null,
)

/**
 * `harvests` est resolu par le serveur : une VARIETE n'a pas de recolte propre et se voit servir
 * celles de son noeud `rollupId`. Le client n'a donc AUCUNE resolution a faire — la liste recue est
 * deja celle qui gouverne les filtres `part`/`inSeason` de `GET /api/v1/trees`.
 */
@Serializable
data class SpeciesDto(
    val id: Long,
    val parentId: Long? = null,
    val rollupId: Long,
    val scientificName: String,
    val commonNameFr: String,
    val form: SpeciesFormDto,
    val selectable: Boolean,
    val harvests: List<SpeciesHarvestDto> = emptyList(),
    // Resolu par `rollupId`, comme `harvests` (ADR 0015) : une VARIETE se voit servir les sosies
    // de son noeud d'espece.
    val lookalikes: List<SpeciesLookalikeDto> = emptyList(),
    // Nullable avec defaut : un build deja publie (ignoreUnknownKeys) ignore ce champ sans casser.
    val photo: SpeciesPhotoDto? = null,
    // Absent = TREE : un ancien serveur ne l'envoie pas, un ancien client l'ignore.
    val scope: SpeciesScopeDto = SpeciesScopeDto.TREE,
)

/**
 * Espece telle qu'elle est portee par un arbre : juste de quoi l'afficher et la filtrer, pas la
 * fiche complete. `TreeDto` embarque cette forme reduite plutot qu'un simple `speciesId` pour que
 * la carte reste affichable sans avoir charge le referentiel — un marqueur sans libelle, le temps
 * d'un appel reseau, serait un regres visible.
 */
@Serializable
data class SpeciesRefDto(
    val id: Long,
    val commonNameFr: String,
    val scientificName: String,
    val form: SpeciesFormDto,
)

@Serializable
data class SpeciesListResponse(
    val species: List<SpeciesDto>,
    val count: Int,
)

/**
 * Une fenetre de recolte peut chevaucher l'annee civile : l'eglantier se ramasse d'octobre a
 * fevrier, soit `monthStart = 10` et `monthEnd = 2`. Un simple `in monthStart..monthEnd` renverrait
 * faux pour decembre et janvier — c'est le bug que ce helper existe pour rendre impossible.
 *
 * Forme brute (colonnes/valeurs primitives) pour etre appelable aussi bien depuis un `SpeciesHarvestDto`
 * que depuis des colonnes Exposed cote `backend-database` (`TreeDao.matchingSpeciesIds`) — une seule
 * definition, partagee, plutot qu'une copie locale qui pourrait diverger silencieusement.
 */
fun isInSeason(
    month: Int,
    monthStart: Int,
    monthEnd: Int,
): Boolean =
    if (monthStart <= monthEnd) {
        month in monthStart..monthEnd
    } else {
        month >= monthStart || month <= monthEnd
    }
